import { Link, useLocation } from 'react-router-dom';
import { LogoImage } from '../../components/Logo';
import { CONTACT } from '../../lib/contact';

/**
 * Public legal pages. Edit COMPANY once to update every policy. These are drafts intended to protect
 * Lakota Software Solutions, Inc.; have qualified counsel review before relying on them — especially
 * the biometric-data and R&D provisions, which are subject to state biometric-privacy laws (e.g. BIPA).
 */
const COMPANY = {
  legalName: 'Lakota Software Solutions, Inc.',
  product: 'EFTCreator',
  site: 'eftcreator.com',
  contactEmail: CONTACT.support,
  privacyEmail: CONTACT.privacy,
  governingState: 'West Virginia',
  effectiveDate: 'July 25, 2026',
};

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  const loc = useLocation();
  const tab = (to: string, label: string) => (
    <Link to={to} className={`legal-tab${loc.pathname === to ? ' is-on' : ''}`}>{label}</Link>
  );
  return (
    <div className="legal-page">
      <header className="legal-bar">
        <Link to="/" aria-label="EFTCreator home"><LogoImage height={26} /></Link>
        <nav className="legal-tabs">
          {tab('/legal/terms', 'Terms of Use')}
          {tab('/legal/privacy', 'Privacy Policy')}
          {tab('/legal/returns', 'Return Policy')}
        </nav>
      </header>
      <main className="legal">
        <h1>{title}</h1>
        <p className="legal-meta">{COMPANY.legalName} · Effective {COMPANY.effectiveDate}</p>
        {children}
        <hr />
        <p className="legal-meta">
          Questions? Contact <a href={`mailto:${COMPANY.contactEmail}`}>{COMPANY.contactEmail}</a>. © {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.
        </p>
      </main>
    </div>
  );
}

export function Terms() {
  return (
    <LegalShell title="Terms of Use">
      <p>These Terms of Use (“Terms”) are a binding agreement between you and the organization you
        represent (“Customer,” “you”) and {COMPANY.legalName} (“Lakota,” “we,” “us”) governing your access
        to and use of the {COMPANY.product} platform, websites, applications, device software, and related
        services (collectively, the “Service”). By creating an account, clicking “I agree,” or using the
        Service, you accept these Terms. If you do not agree, do not use the Service. If you accept on
        behalf of an organization, you represent that you are authorized to bind it.</p>

      <h2>1. The Service</h2>
      <p>{COMPANY.product} helps authorized users capture, validate, and transmit fingerprint-based
        Electronic Fingerprint Transmissions (EFTs) and related submissions to third-party agencies and
        systems (including, without limitation, the FBI, ATF, and U.S. Department of Defense/SWFT). Lakota
        is an independent software provider and is <strong>not</strong> a government agency, and is not
        affiliated with, endorsed by, or acting on behalf of any agency. We do not guarantee that any
        submission will be accepted, processed, or acted upon by any agency, or that any particular result
        or turnaround will be achieved. Agency requirements, availability, and decisions are outside our
        control.</p>

      <h2>2. Accounts &amp; Eligibility</h2>
      <p>You must be at least 18 years old and provide accurate, complete account information and keep it
        current. You are responsible for safeguarding your credentials and for all activity under your
        account. Notify us immediately of any unauthorized use. You are responsible for your users and
        their compliance with these Terms.</p>

      <h2>3. Customer Responsibilities &amp; Acceptable Use</h2>
      <p>You represent and warrant that you have all necessary rights, authorizations, and lawful bases —
        including any required consent from each individual whose fingerprints, images, or personal
        information you submit — to collect, upload, process, and transmit such data through the Service.
        You are solely responsible for the accuracy, legality, and appropriateness of all data you submit
        and for compliance with all applicable laws and agency requirements (including ATF, FBI CJIS, and
        DoD/SWFT requirements). You will not: (a) use the Service for any unlawful, fraudulent, or
        unauthorized purpose; (b) submit data you are not authorized to submit; (c) attempt to gain
        unauthorized access to, interfere with, reverse engineer, or disrupt the Service; (d) resell or
        provide the Service to third parties except as expressly permitted; or (e) misuse device licenses
        (including circumventing licensing, cooldown, or seat controls).</p>

      <h2>4. Fees, Subscriptions &amp; Billing</h2>
      <p>Paid plans, subscriptions, device-license seats, storage plans, card-conversion credits, and
        hardware are billed through our payment processor. Unless stated otherwise: subscriptions
        <strong> automatically renew</strong> each billing period until cancelled; adding a scanner or seat
        is <strong>prorated and charged immediately</strong>; reductions and cancellations take effect at
        the <strong>end of the current billing period with no mid-cycle credit or refund</strong>; and all
        fees are exclusive of taxes, which you are responsible for. Fees are non-refundable except as
        expressly provided in our <Link to="/legal/returns">Return Policy</Link>. We may change pricing
        prospectively; changes apply at your next renewal. If a charge fails, we may suspend paid features
        until the balance is paid.</p>

      <h2>5. Device Licenses</h2>
      <p>Per-device (unlimited) plans include one licensed scanner; additional scanners are licensed via a
        separate add-on. To prevent abuse, a license released from a scanner is subject to a re-binding
        cooldown before it may be assigned to a different scanner, and replaced units may be retired. These
        controls are part of the Service and may be updated from time to time.</p>

      <h2>6. Intellectual Property</h2>
      <p>The Service, including all software, models, algorithms (including our fingerprint card-recognition
        and segmentation technology), designs, and content, is owned by Lakota and its licensors and is
        protected by law. We grant you a limited, non-exclusive, non-transferable, revocable license to use
        the Service for your internal business purposes during your subscription. We reserve all rights not
        expressly granted. If you provide feedback or suggestions, you grant Lakota a perpetual, worldwide,
        royalty-free license to use them without restriction.</p>

      <h2>7. Customer Data</h2>
      <p>As between the parties, you retain ownership of the data you submit. You grant Lakota a worldwide,
        non-exclusive license to host, process, transmit, display, and otherwise use your data to provide,
        secure, and improve the Service and as described in our <Link to="/legal/privacy">Privacy Policy</Link>,
        including use for internal research, development, analytics, and improvement of our products and
        models. Your use of the Service and our handling of personal and biometric information are governed
        by the Privacy Policy, which is incorporated into these Terms.</p>

      <h2>8. Third-Party Services</h2>
      <p>The Service integrates with third parties (e.g., payment processing, cloud hosting, email, support,
        and agency systems). Your use of those services may be subject to their terms, and Lakota is not
        responsible for third-party acts, omissions, availability, or content.</p>

      <h2>9. Disclaimers</h2>
      <p>THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
        IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, TITLE, AND NON-INFRINGEMENT. LAKOTA DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
        ERROR-FREE, OR SECURE, OR THAT ANY SUBMISSION WILL BE ACCEPTED OR PROCESSED BY ANY AGENCY.</p>

      <h2>10. Limitation of Liability</h2>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, LAKOTA AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND SUPPLIERS
        WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
        DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, OR GOODWILL, ARISING OUT OF OR RELATING TO THE SERVICE.
        LAKOTA’S TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE WILL NOT EXCEED THE
        AMOUNTS YOU PAID TO LAKOTA FOR THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE
        TO THE CLAIM. THESE LIMITATIONS APPLY REGARDLESS OF THE THEORY OF LIABILITY AND EVEN IF A REMEDY
        FAILS OF ITS ESSENTIAL PURPOSE.</p>

      <h2>11. Indemnification</h2>
      <p>You will defend, indemnify, and hold harmless Lakota and its affiliates from and against any claims,
        damages, liabilities, losses, and expenses (including reasonable attorneys’ fees) arising out of or
        relating to: (a) your data or submissions; (b) your use of the Service; (c) your violation of these
        Terms or applicable law; or (d) your failure to obtain required consents or authorizations,
        including with respect to fingerprints and biometric information.</p>

      <h2>12. Suspension &amp; Termination</h2>
      <p>We may suspend or terminate access for non-payment, suspected misuse, security risk, or violation of
        these Terms. You may cancel at any time; cancellation takes effect at period end. Upon termination,
        your license ends and we may delete data subject to the retention terms in the Privacy Policy and
        applicable law. Sections that by their nature should survive (including IP, disclaimers, liability,
        indemnification, and dispute resolution) survive termination.</p>

      <h2>13. Governing Law &amp; Dispute Resolution</h2>
      <p>These Terms are governed by the laws of the State of {COMPANY.governingState}, without regard to
        conflict-of-laws rules. The parties will attempt to resolve disputes informally. Any dispute not
        resolved will be subject to binding arbitration on an individual basis, and you and Lakota
        <strong> waive any right to a jury trial and to participate in a class or representative action</strong>,
        to the extent permitted by law. Exclusive venue for any permitted court proceeding lies in the state
        or federal courts located in {COMPANY.governingState}.</p>

      <h2>14. Changes to these Terms</h2>
      <p>We may update these Terms from time to time. Material changes will be posted here with an updated
        effective date and, where appropriate, additional notice. Your continued use after changes take
        effect constitutes acceptance.</p>

      <h2>15. Miscellaneous</h2>
      <p>These Terms and the Privacy and Return Policies are the entire agreement between the parties
        regarding the Service and supersede prior agreements. If any provision is unenforceable, the rest
        remains in effect. You may not assign these Terms without our consent; we may assign them in
        connection with a merger, acquisition, or sale of assets. Neither party is liable for delays or
        failures due to causes beyond its reasonable control.</p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy">
      <p>This Privacy Policy explains how {COMPANY.legalName} (“Lakota,” “we”) collects, uses, discloses, and
        retains information in connection with the {COMPANY.product} Service. By using the Service you consent
        to the practices described here. This Policy is incorporated into our <Link to="/legal/terms">Terms of
        Use</Link>.</p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li><strong>Account &amp; identity data:</strong> name, email, organization, role, addresses (physical
          and billing), and credentials.</li>
        <li><strong>Biometric &amp; submission data:</strong> fingerprint images, quality scores, and the
          EFT/EBTS files you create, along with the applicant/subject personal information you enter (e.g.,
          name, date of birth, and other fields required by the relevant transaction type).</li>
        <li><strong>Payment data:</strong> processed by our payment processor (Stripe). We do not store full
          card numbers; we retain billing metadata such as plan, status, and transaction identifiers.</li>
        <li><strong>Device &amp; technical data:</strong> scanner identifiers, device-service version,
          installation identifiers, IP address, log and usage data.</li>
        <li><strong>Support &amp; communications:</strong> messages, tickets, and related context you provide.</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <p>We use information to: provide, operate, secure, and maintain the Service; capture, validate, and
        transmit your submissions to the agencies and systems you direct; process payments and manage
        subscriptions; provide support and send service and, where permitted, marketing communications;
        detect, prevent, and address fraud, abuse, and security issues; comply with legal obligations and
        agency requirements; and for the research and development purposes described below.</p>

      <h2>3. Research, Development &amp; Product Improvement</h2>
      <p>You acknowledge and agree that Lakota may use data stored or processed through the Service —
        including account, usage, submission, and biometric-related data, and including personal information —
        for its internal business purposes, including <strong>research and development, analytics, quality
        assurance, benchmarking, and to develop, train, test, evaluate, and improve Lakota’s products,
        services, software, features, and machine-learning models</strong> (including our fingerprint
        card-recognition and segmentation technology). Wherever reasonably practicable, we use de-identified
        or aggregated data for these purposes. <strong>We may create, retain, and use de-identified and
        aggregated data indefinitely</strong>, including after your account is closed, and such data is not
        subject to deletion requests. We do not sell personal or biometric information, and we do not use it
        for third-party advertising.</p>
      <p><strong>Your R&amp;D opt-out.</strong> An account administrator may opt your account out of having its
        data used for research, development, and model-improvement purposes at any time under{' '}
        <strong>My Profile → Data &amp; privacy</strong>. When you opt out, we exclude your account’s data
        (including biometric data) from these purposes going forward. We will still process your data as
        necessary to provide the Service, meet legal and security obligations, and as otherwise described in
        this Policy, and any de-identified or aggregated data already created may continue to be used as
        described above.</p>

      <h2>4. Biometric Information Notice</h2>
      <p>The Service processes fingerprint images and related biometric identifiers <strong>solely to enable
        you to capture, validate, store (if you subscribe to storage), and transmit</strong> EFT submissions
        at your direction, and for the security, service-improvement, and research-and-development purposes
        described in this Policy. <strong>You are responsible for obtaining any legally required notice and
        consent from each individual</strong> whose biometric information you submit. We retain biometric and
        submission data according to Section 6 and your storage settings, and we apply reasonable safeguards
        to protect it. We do not sell or lease biometric identifiers or profit from them by selling them to
        third parties.</p>

      <h2>5. How We Share Information</h2>
      <ul>
        <li><strong>Agencies &amp; recipients you direct:</strong> the FBI, ATF, DoD/SWFT, channelers, and
          similar systems to which you submit transactions.</li>
        <li><strong>Service providers (subprocessors):</strong> cloud hosting and AI/ML infrastructure
          (Amazon Web Services, including Amazon Bedrock), payment processing (Stripe), email (Amazon SES),
          and support tooling — bound to protect the data and use it only to provide services to us.</li>
        <li><strong>Legal &amp; safety:</strong> to comply with law, legal process, or lawful requests, and to
          protect the rights, safety, and property of Lakota, our customers, or others.</li>
        <li><strong>Business transfers:</strong> in connection with a merger, acquisition, financing, or sale
          of assets.</li>
        <li><strong>Aggregated/de-identified data:</strong> which does not identify you or any individual, may
          be used and shared without restriction.</li>
      </ul>

      <h2>6. Data Retention</h2>
      <p>We retain account and submission data for as long as your account is active and as needed to provide
        the Service. Stored EFTs are retained per your storage plan. For customers without an active storage
        plan, completed card-conversion outputs are retained for a limited period (e.g., 30 days or until
        downloaded) and then deleted. We may retain information as required by law, for legitimate business
        purposes, and to enforce agreements, and we may retain de-identified/aggregated data as described in
        Section 3. Backups are cycled on a rolling basis.</p>

      <h2>7. Security</h2>
      <p>We use administrative, technical, and physical safeguards designed to protect information, including
        encryption in transit and at rest and access controls. No method of transmission or storage is
        completely secure, and we cannot guarantee absolute security.</p>

      <h2>8. Your Choices &amp; Rights</h2>
      <p>You can review and update account information, addresses, and email in your profile, and opt out of
        marketing email via the unsubscribe link (transactional messages will continue). Depending on your
        jurisdiction, you may have rights to access, correct, delete, or restrict certain personal
        information; to exercise them, contact <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>.
        Some data cannot be deleted where retention is required by law or agency rules, is necessary to
        provide the Service, or has been de-identified or aggregated.</p>

      <h2>9. Children</h2>
      <p>The Service is intended for business use by adults and is not directed to children. Note that
        submissions you process may include information about individuals (including, where lawfully
        authorized, minors) whom you are responsible for handling appropriately.</p>

      <h2>10. Changes</h2>
      <p>We may update this Policy from time to time; material changes will be posted here with a new
        effective date. Your continued use after changes take effect constitutes acceptance.</p>

      <h2>11. Contact</h2>
      <p>Privacy questions: <a href={`mailto:${COMPANY.privacyEmail}`}>{COMPANY.privacyEmail}</a>.</p>
    </LegalShell>
  );
}

export function Returns() {
  return (
    <LegalShell title="Return &amp; Refund Policy">
      <p>This Return &amp; Refund Policy applies to purchases made through {COMPANY.product}. Please review it
        carefully; by purchasing, you agree to these terms.</p>

      <h2>1. Hardware (scanners &amp; supplies)</h2>
      <p>Eligible hardware may be returned within <strong>30 days</strong> of delivery, provided it is unused,
        in resalable condition, and in its original packaging with all accessories. A <strong>restocking fee
        of up to 15%</strong> may apply, and original shipping charges are non-refundable. Return shipping is
        the customer’s responsibility unless the item arrived defective or was sent in error.</p>
      <p><strong>Defective or dead-on-arrival units:</strong> contact us within 30 days for a replacement or
        repair under the applicable manufacturer warranty; some items may require handling directly through
        the manufacturer’s warranty process. Certain items may be non-returnable for hygiene, licensing, or
        safety reasons; such items are identified at purchase.</p>

      <h2>2. Software, subscriptions &amp; digital items</h2>
      <p>Except where required by law, fees for subscriptions, device-license seats, secure-storage plans, and
        card-conversion credits are <strong>non-refundable</strong>, including partial billing periods. You
        may cancel at any time; cancellation and seat/plan reductions take effect at the end of the current
        billing period, and <strong>no mid-cycle credits or refunds</strong> are issued. Adding a scanner or
        seat is prorated and charged immediately and is non-refundable once added. Card-conversion credits are
        non-refundable once purchased and are void on account termination.</p>

      <h2>3. How to request a return</h2>
      <p>Contact <a href={`mailto:${COMPANY.contactEmail}`}>{COMPANY.contactEmail}</a> with your order number to
        request a return authorization. Returns sent without authorization may be refused. Approved refunds
        are issued to the original payment method within a reasonable time after we receive and inspect the
        returned item.</p>

      <h2>4. Changes</h2>
      <p>We may update this Policy from time to time; the version in effect at the time of your purchase
        applies to that purchase.</p>
    </LegalShell>
  );
}
